import React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useAppSelector } from "hooks/useAppSelector";
import { authStatusSelector } from "reduxStore/auth/selectors";
import { getValidationSchema, initialValues } from "./config";
import { useAppDispatch } from "hooks/useAppDispatch";
import { StyledFormicError } from "modals/ResetPasswordModal/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setPasswordThunk } from "reduxStore/auth/thunks";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { Messages, NotificationSeverity } from "helpers/constants";
import { IResetPasswordCredentials } from "types/auth";
import { AuthContainer } from "components/Layout/AuthContainer";
import { Password } from "../../components/Password";

const SetPasswordWrapper: React.FC = () => {
  const statuses = useAppSelector(authStatusSelector);
  const navigate = useNavigate();
  const error = statuses["auth/setPasswordThunk"]?.error;
  const loading = statuses["auth/setPasswordThunk"]?.pending;
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const handleSubmit = async (values: IResetPasswordCredentials) => {
    const { payload } = await dispatch(setPasswordThunk(values));
    if (payload === "authenticated") {
      dispatch(
        showNotificationThunk({
          message: Messages.successSetPassword,
          severity: NotificationSeverity.success,
        }),
      );
      window.location.reload();
    } else if (payload === "not authenticated") {
      dispatch(
        showNotificationThunk({
          message: Messages.successSetPassword,
          severity: NotificationSeverity.success,
        }),
      );
      navigate("/");
    } else {
      dispatch(
        showNotificationThunk({
          message: Messages.errorSetPassword,
          severity: NotificationSeverity.error,
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: getValidationSchema,
    onSubmit: (values) => {
      const data = {
        password: values.password,
        username: searchParams.get("email") ?? "",
        code: searchParams.get("code") ?? "",
      };
      handleSubmit(data).then();
    },
  });

  return (
    <AuthContainer loading={loading}>
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h6">
          Set Password
        </Typography>

        <Typography component="h1" variant="subtitle2" align={"center"}>
          Please, for security reasons, set a new password to access your
          account.
        </Typography>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
          <Password
            id="password"
            onChange={formik.handleChange}
            name="password"
            label="Password"
            required={true}
          />

          <Password
            id="repeatPassword"
            onChange={formik.handleChange}
            name="repeatPassword"
            label="Repeat password"
            required={true}
          />

          <Box sx={{ m: 1, position: "relative" }}>
            <Button
              disabled={loading}
              type="submit"
              fullWidth
              color="primary"
              variant="contained"
              sx={{ mt: 3, mb: 0 }}
            >
              Submit
            </Button>
            {loading && (
              <CircularProgress
                size={30}
                sx={{
                  color: green[500],
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
          <Box sx={{ m: 1, position: "relative" }}>
            <Button
              disabled={loading}
              fullWidth
              color="primary"
              variant="text"
              sx={{ mt: 0, mb: 2 }}
              onClick={() => window.location.replace("/reset-request")}
            >
              Request New Code
            </Button>
          </Box>
          {error && <StyledFormicError>{error}</StyledFormicError>}
        </Box>
      </Box>
    </AuthContainer>
  );
};

export default SetPasswordWrapper;
